import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { AUDIO_EXTENSIONS, VIDEO_EXTENSIONS, IMAGE_EXTENSIONS } from '../../utils/mediaTypeDetection';

interface FileUploadProps {
  label: string;
  currentUrl?: string | null;
  onUpload: (file: File) => Promise<void>;
  onDelete?: () => void;
  accept?: string;
  maxSize?: number; // in bytes
  disabled?: boolean;
  uploadingText?: string;
  deleteText?: string;
  replaceText?: string;
  confirmDeleteText?: string;
  fileUploadedText?: string;
  dropFileHereText?: string;
  dragDropText?: string;
  uploadErrorText?: string;
}

export default function FileUpload({
  label,
  currentUrl,
  onUpload,
  onDelete,
  accept = 'image/*',
  maxSize = 5 * 1024 * 1024, // 5MB default
  disabled = false,
  uploadingText = 'Uploading...',
  deleteText = 'Delete',
  replaceText = 'Replace',
  confirmDeleteText = 'Delete this media?',
  fileUploadedText = 'File uploaded',
  dropFileHereText = 'Drop file here...',
  dragDropText = 'Drag file here or click to select',
  uploadErrorText = 'File upload error'
}: FileUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setError(null);
    setUploading(true);

    try {
      await onUpload(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : uploadErrorText);
    } finally {
      setUploading(false);
    }
  }, [onUpload, uploadErrorText]);

  const acceptMap: Record<string, string[]> = (() => {
    const map: Record<string, string[]> = {};
    for (const part of accept.split(',').map(s => s.trim()).filter(Boolean)) {
      if (part === 'audio/*') {
        map['audio/*'] = AUDIO_EXTENSIONS.map(e => `.${e}`);
      } else if (part === 'video/*') {
        map['video/*'] = VIDEO_EXTENSIONS.map(e => `.${e}`);
      } else if (part === 'image/*') {
        map['image/*'] = IMAGE_EXTENSIONS.map(e => `.${e}`);
      } else {
        map[part] = [];
      }
    }
    return map;
  })();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptMap,
    maxSize,
    multiple: false,
    disabled: disabled || uploading
  });

  const handleDelete = () => {
    if (onDelete && confirm(confirmDeleteText)) {
      onDelete();
    }
  };

  const urlExt = currentUrl ? (currentUrl.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() ?? '') : '';
  const isAudio = !!currentUrl && (AUDIO_EXTENSIONS.includes(urlExt) || (accept.includes('audio/') && !urlExt));
  const isImage = !!currentUrl && !isAudio && (
    IMAGE_EXTENSIONS.includes(urlExt) ||
    (accept.includes('image/') && !urlExt)
  );
  const isVideo = !!currentUrl && !isAudio && !isImage && (
    VIDEO_EXTENSIONS.includes(urlExt) ||
    (accept.includes('video/') && !urlExt)
  );

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-300">
        {label}
      </label>

      {currentUrl ? (
        <div className="space-y-2">
          {/* Preview */}
          <div className="relative bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
            {isImage && (
              <img
                src={currentUrl}
                alt="Preview"
                className="w-full h-48 object-contain"
              />
            )}
            {isVideo && (
              <video
                src={currentUrl}
                className="w-full h-64 object-contain bg-gray-900"
                controls
                preload="metadata"
              />
            )}
            {isAudio && (
              <div className="w-full h-48 flex flex-col items-center justify-center gap-3 px-4 bg-gray-900">
                <svg className="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19a3 3 0 11-6 0 3 3 0 016 0zm12-3a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <audio src={currentUrl} controls preload="metadata" className="w-full" />
              </div>
            )}
            {!isImage && !isVideo && !isAudio && (
              <div className="w-full h-48 flex items-center justify-center text-gray-400">
                <span>{fileUploadedText}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleDelete}
              disabled={disabled || uploading}
              className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleteText}
            </button>
            <div
              {...getRootProps()}
              className="flex-1"
            >
              <input {...getInputProps()} />
              <button
                type="button"
                disabled={disabled || uploading}
                className="w-full px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? uploadingText : replaceText}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
            transition-colors
            ${isDragActive
              ? 'border-blue-500 bg-blue-500/10'
              : 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
            }
            ${(disabled || uploading) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />
          <div className="space-y-2">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {uploading ? (
              <p className="text-sm text-gray-400">{uploadingText}</p>
            ) : (
              <>
                <p className="text-sm text-gray-400">
                  {isDragActive ? dropFileHereText : dragDropText}
                </p>
                <p className="text-xs text-gray-500">
                  {accept === 'image/*' && `PNG, JPG, WEBP up to ${Math.round(maxSize / 1024 / 1024)}MB`}
                  {accept === 'video/*' && `MP4, WEBM, MOV, MKV, AVI, M4V up to ${Math.round(maxSize / 1024 / 1024)}MB`}
                  {accept === 'audio/*' && `MP3, WAV, OGG, FLAC, M4A, AAC up to ${Math.round(maxSize / 1024 / 1024)}MB`}
                  {!accept.includes('image/') && !accept.includes('video/') && !accept.includes('audio/') &&
                    `Max ${Math.round(maxSize / 1024 / 1024)}MB`
                  }
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}
    </div>
  );
}
